/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { InstanceData } from "./instance-data";

export enum PropertyType {
  ANTENNA_REFERENCE = "ANTENNA_REFERENCE",
  BOOLEAN = "BOOLEAN",
  CHARACTER = "CHARACTER",
  COLOR = "COLOR",
  DATE = "DATE",
  DAY = "DAY",
  DOUBLE = "DOUBLE",
  DRIVEROBJECT_REFERENCE = "DRIVEROBJECT_REFERENCE",
  EMAIL = "EMAIL",
  EVENT_REFERENCE = "EVENT_REFERENCE",
  EVENTINST_REFERENCE = "EVENTINST_REFERENCE",
  FLOAT = "FLOAT",
  GROUP_REFERENCE = "GROUP_REFERENCE",
  IMAGE_REFERENCE = "IMAGE_REFERENCE",
  INTEGER = "INTEGER",
  LOCATION_REFERENCE = "LOCATION_REFERENCE",
  LONG = "LONG",
  MONTH = "MONTH",
  OBJECT_REFERENCE = "OBJECT_REFERENCE",
  PHONE_NUMBER = "PHONE_NUMBER",
  POINT = "POINT",
  READER_REFERENCE = "READER_REFERENCE",
  SERVEROBJECT_REFERENCE = "SERVEROBJECT_REFERENCE",
  SERVICE_REFERENCE = "SERVICE_REFERENCE",
  SHORT = "SHORT",
  STRING = "STRING",
  TAG_REFERENCE = "TAG_REFERENCE",
  TIME = "TIME",
  TIMESTAMP = "TIMESTAMP",
  USER_REFERENCE = "USER_REFERENCE",
  ZONE_REFERENCE = "ZONE_REFERENCE",
  OBJECTTYPE_REFERENCE = "OBJECTTYPE_REFERENCE"
  
}

export class PropertyTypeUtil {

    /**
     * This should be called after obtaining an "InstanceData" from the Server.  This fixes some issues
     * related to the way the "object_value" property is handled. 
     * 
     * The way we handle the possible values around the "object_type" parameter of the "InstanceData" is 
     * different between the backend / frontend.  In the backend, it is sometimes a String / Boolean / Number of JSON object containing 
     * a key / reference to whatever it is being represented: Example: 'object_value': { 'character': 'T' }
     * 
     * The "fixIncomingInstanceDataObjectValueForType(PropertyType, InstanceData)" method pulls things up to that top
     * "object_value" level, in our Example: "'object_value': { 'character': 'T' }" becomes: "'object_value': 'T'".
     * 
     * The "fixOutgoingInstanceDataObjectValueForType(PropertyType, InstanceData)" method is supposed to reverse it so that it is 
     * in the format the backend would expect, but I was having some issues getting that to work like that.  It needs to be looked 
     * into but for now I'm just going to do what the old handling was doing.
     * (just not send the "object_value" property and only use the "value" property).
     */
    static fixIncomingInstanceDataObjectValueForType(propertyType: PropertyType, instanceData: InstanceData): void {
        switch (propertyType) {
            case PropertyType.ANTENNA_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.antenna_reference;
                break;
            }
            case PropertyType.CHARACTER: {
                instanceData.object_value = instanceData?.object_value?.character;
                break;
            }
            case PropertyType.DRIVEROBJECT_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.driver_object_reference;
                break;
            }
            case PropertyType.FLOAT: {
                instanceData.object_value = instanceData?.object_value?.float;
                break;
            }
            case PropertyType.GROUP_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.group_reference;
                break;
            }
            case PropertyType.IMAGE_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.Image;
                break;
            }
            case PropertyType.LOCATION_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.location_reference;
                break;
            }
            case PropertyType.LONG: {
                instanceData.object_value = instanceData?.object_value?.long;
                break;
            }
            case PropertyType.OBJECT_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.object_reference;
                break;
            }
            case PropertyType.READER_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.reader_reference;
                break;
            }
            case PropertyType.SERVEROBJECT_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.server_object_reference;
                break;
            }
            case PropertyType.SERVICE_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.service_reference;
                break;
            }
            case PropertyType.SHORT: {
                instanceData.object_value = instanceData?.object_value?.short;
                break;
            }
            case PropertyType.TAG_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.tag_reference;
                break;
            }
            case PropertyType.USER_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.user_reference;
                break;
            }
            case PropertyType.ZONE_REFERENCE: {
                instanceData.object_value = instanceData?.object_value?.zone_reference;
                break;
            }
			case PropertyType.OBJECTTYPE_REFERENCE: {
			    instanceData.object_value = instanceData?.object_value?.objecttype_reference;
			    break;
			}
            default: {
                break;
            }
        }

        // Fixes issue where the "NULL" option is not being selected by default
        if (instanceData.object_value == undefined) {
            instanceData.object_value = null;
        }
    }

    /**
     * This should be called before sending an "InstanceData" to the Server to be created / updated.
     * 
     * The way we handle the possible values around the "object_type" parameter of the "InstanceData" is 
     * different between the backend / frontend.  In the backend, it is sometimes a String / Boolean / Number of JSON object containing 
     * a key / reference to whatever it is being represented: Example: 'object_value': { 'character': 'T' }
     * 
     * The "fixIncomingInstanceDataObjectValueForType(PropertyType, InstanceData)" method pulls things up to that top
     * "object_value" level, in our Example: "'object_value': { 'character': 'T' }" becomes: "'object_value': 'T'".
     * 
     * The "fixOutgoingInstanceDataObjectValueForType(PropertyType, InstanceData)" method is supposed to reverse it so that it is 
     * in the format the backend would expect, but I was having some issues getting that to work like that.  It needs to be looked 
     * into but for now I'm just going to do what the old handling was doing.
     * (just not send the "object_value" property and only use the "value" property).
     */
    static fixOutgoingInstanceDataObjectValueForType(propertyType: PropertyType, instanceData: InstanceData): void {
        /**
         * The following does the exact opposite of the "fixIncomingInstanceDataObjectValueForType(PropertyType, InstanceData)".
         * This should be needed when sending the Data back to the Server for updating / handling as it should fix the 
         * structural changes that we needed for the incoming JSON data.  
         * 
         * However, when I tested this, I am still seeing "c.c.e.m.e.EmEntityData - Invalid Valid for Type. Expected xxxx" errors 
         * event with it in.  I'm going to just default to setting the "object_value" to undefined so it isn't sent as this is what
         * the old UI was doing, but eventually we should trace down what the APIs are expecting here...
         */
        /*
        if (instanceData?.object_value != null) {
            switch (propertyType) {
                case PropertyType.ANTENNA_REFERENCE: {
                    instanceData.object_value = { 'antenna_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.CHARACTER: {
                    instanceData.object_value = { 'character': instanceData.object_value };
                    break;
                }
                case PropertyType.DRIVEROBJECT_REFERENCE: {
                    instanceData.object_value = { 'driver_object_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.FLOAT: {
                    instanceData.object_value = { 'float': instanceData.object_value };
                    break;
                }
                case PropertyType.GROUP_REFERENCE: {
                    instanceData.object_value = { 'group_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.IMAGE_REFERENCE: {
                    instanceData.object_value = { 'Image': instanceData.object_value };
                    break;
                }
                case PropertyType.LOCATION_REFERENCE: {
                    instanceData.object_value = { 'location_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.LONG: {
                    instanceData.object_value = { 'long': instanceData.object_value };
                    break;
                }
                case PropertyType.OBJECT_REFERENCE: {
                    instanceData.object_value = { 'object_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.READER_REFERENCE: {
                    instanceData.object_value = { 'reader_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.SERVEROBJECT_REFERENCE: {
                    instanceData.object_value = { 'server_object_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.SERVICE_REFERENCE: {
                    instanceData.object_value = { 'service_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.SHORT: {
                    instanceData.object_value = { 'short': instanceData.object_value };
                    break;
                }
                case PropertyType.TAG_REFERENCE: {
                    instanceData.object_value = { 'tag_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.USER_REFERENCE: {
                    instanceData.object_value = { 'user_reference': instanceData.object_value };
                    break;
                }
                case PropertyType.ZONE_REFERENCE: {
                    instanceData.object_value = { 'zone_reference': instanceData.object_value };
                    break;
                }
                default: {
                    break;
                }
            }
        }
        if (instanceData.object_value == null) {
            instanceData.object_value = undefined;
        }
        */

        // Just set the "object_value" to undefined so that it is not sent
        instanceData.object_value = undefined;
    }

    static isNumber(propertyType: PropertyType): boolean {
        switch (propertyType) {
            case PropertyType.DOUBLE:
            case PropertyType.FLOAT:
            case PropertyType.INTEGER:
            case PropertyType.SHORT:
                return true;
            default:
                return false;
        }
    }

    static isString(propertyType: PropertyType): boolean {
        switch (propertyType) {
            case PropertyType.CHARACTER:
            case PropertyType.STRING:
                return true;
            default:
                return false;
        }
    }

    static isReference(propertyType: PropertyType): boolean {
        switch (propertyType) {
            case PropertyType.ANTENNA_REFERENCE:
            case PropertyType.DRIVEROBJECT_REFERENCE:
            case PropertyType.EVENT_REFERENCE:
            case PropertyType.GROUP_REFERENCE:
            case PropertyType.IMAGE_REFERENCE:
            case PropertyType.LOCATION_REFERENCE:
            case PropertyType.OBJECT_REFERENCE:
            case PropertyType.READER_REFERENCE:
            case PropertyType.SERVEROBJECT_REFERENCE:
            case PropertyType.SERVICE_REFERENCE:
            case PropertyType.TAG_REFERENCE:
            case PropertyType.USER_REFERENCE:
            case PropertyType.ZONE_REFERENCE:
			case PropertyType.OBJECTTYPE_REFERENCE:
                return true;
            default:
                return false;
        }
    }

    static getIconClass(propertyType: PropertyType): string {
        switch (propertyType) {
            case PropertyType.BOOLEAN:
                return "fa fa-toggle-on";
            case PropertyType.CHARACTER:
                return "fa fa-font";
            case PropertyType.COLOR:
                return "fa fa-paint-brush";
            case PropertyType.DATE:
                return "fa fa-calendar-check-o";
            case PropertyType.DAY:
                return "fa fa-calendar-o";
            case PropertyType.DOUBLE:
            case PropertyType.FLOAT:
            case PropertyType.INTEGER:
            case PropertyType.LONG:
            case PropertyType.SHORT:
                return "fa fa-font";
            case PropertyType.MONTH:
                return "fa fa-calendar";
            case PropertyType.STRING:
                return "fa fa-quote-right";
            case PropertyType.TIME:
            case PropertyType.TIMESTAMP:
                return "fa fa-clock-o";
            case PropertyType.ANTENNA_REFERENCE:
                return "fa fa-feed";
            case PropertyType.EVENT_REFERENCE:
                return "fa fa-bolt";
            case PropertyType.GROUP_REFERENCE:
                return "fas fa-sitemap";
            case PropertyType.IMAGE_REFERENCE:
                return "fa fa-image";
            case PropertyType.LOCATION_REFERENCE:
                return "fa fa-location-arrow";
            case PropertyType.OBJECT_REFERENCE:
                return "fa fa-circle";
            case PropertyType.READER_REFERENCE:
                return "fa fa-tty";
            case PropertyType.TAG_REFERENCE:
                return "fa fa-tag";
            case PropertyType.USER_REFERENCE:
                return "fa fa-user";
            case PropertyType.ZONE_REFERENCE:
                return "fa fa-bullseye";
            default:
                return "fas fa-genderless";
        }
    }

}
